import React, { useState, useEffect } from 'react';
import { Download, GearSix, Copy, Check } from '@phosphor-icons/react';
import PageHeader from '../../../shared/components/ui/PageHeader';
import GenericFilters from '../../../shared/components/ui/GenericFilters';
import GenericTables from '../../../shared/components/ui/GenericTables';
import { useResponsive } from '../../../hooks/useResponsive';

const getTodayLocalDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    userRole: '',
    startDate: getTodayLocalDate(),
    endDate: ''
  });
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedDescriptionKey, setCopiedDescriptionKey] = useState(null);
  const [expandedTextKey, setExpandedTextKey] = useState(null);
  const { width, isTouchDevice } = useResponsive();
  const isNotebookOrSmaller = width > 0 && width <= 1366;
  const isMobileOnly = width > 0 && width < 768;

  const getLogCopyKey = (log) => {
    return log.id || log._id || `${log.createdAt || ''}-${log.userName || ''}-${log.action || ''}-${log.description || ''}`;
  };

  const createTextCellKey = (prefix, log) => `${prefix}-${getLogCopyKey(log)}`;

  const handleTextCellToggle = (cellKey) => {
    setExpandedTextKey((current) => (current === cellKey ? null : cellKey));
  };

  const renderResponsiveText = ({
    log,
    value,
    maxLength,
    prefix,
    className = '',
    alwaysTruncate = false
  }) => {
    const text = value === null || value === undefined ? '-' : String(value);
    const cellKey = createTextCellKey(prefix, log);
    const shouldTruncate = text !== '-' && text.length > maxLength && (alwaysTruncate || isNotebookOrSmaller);
    const isExpanded = expandedTextKey === cellKey;
    const displayText = shouldTruncate && !isExpanded ? truncateText(text, maxLength) : text;
    const interactive = shouldTruncate && isMobileOnly;

    const sharedProps = interactive
      ? {
          role: 'button',
          tabIndex: 0,
          onClick: () => handleTextCellToggle(cellKey),
          onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleTextCellToggle(cellKey);
            }
          }
        }
      : {};

    return (
      <span
        title={text}
        className={`${className} ${interactive ? 'cursor-pointer' : 'cursor-help'}`}
        {...sharedProps}
      >
        {displayText}
      </span>
    );
  };

  const handleCopyDescription = async (log) => {
    const descriptionText = log.description ? String(log.description) : '';
    if (!descriptionText) return;

    let success = false;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(descriptionText);
        success = true;
      } catch (err) {
        console.warn('Clipboard API failed, trying fallback:', err);
      }
    }

    if (!success) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = descriptionText;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);

        textarea.select();
        textarea.setSelectionRange(0, descriptionText.length);

        success = document.execCommand('copy');
        document.body.removeChild(textarea);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
    }

    if (success) {
      const copyKey = getLogCopyKey(log);
      setCopiedDescriptionKey(copyKey);
      setTimeout(() => {
        setCopiedDescriptionKey((current) => current === copyKey ? null : current);
      }, 2000);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      const sizeValue = parseInt(pageSize, 10) || 50;
      const pageValue = parseInt(currentPage, 10) || 1;
      
      // Backend espera page 0-indexed: page 1 → 0, page 2 → 1, etc
      params.append('s', sizeValue);
      params.append('p', pageValue - 1);

      if (filters.action) params.append('action', filters.action);
      if (filters.userRole) params.append('userRole', filters.userRole);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      const fetchResponse = await fetch(`/api/audit/activity-logs?${params.toString()}`, {
        method: 'GET',
        headers: {
					'Content-Type': 'application/json',
				},
      });

      if (!fetchResponse.ok) {
        throw new Error(`HTTP error! status: ${fetchResponse.status}`);
      }

      const response = await fetchResponse.json();

      const rows = Array.isArray(response?.logs)
        ? response.logs
        : Array.isArray(response?.responses)
          ? response.responses
          : [];

      const totalFromHeader = Number(fetchResponse.headers.get('x-total-count'));
      const contentRange = fetchResponse.headers.get('content-range');
      const totalFromContentRange = contentRange ? Number(contentRange.split('/')[1]) : NaN;
      const totalFromBody = Number(
        response?.total ?? response?.totalItems ?? response?.pagination?.totalItems
      );
      const resolvedTotal = [totalFromBody, totalFromHeader, totalFromContentRange].find(Number.isFinite);

      setLogs(rows);
      setTotal(Number.isFinite(resolvedTotal) ? resolvedTotal : 0);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filters, currentPage, pageSize]);

  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'CREATE':
        return 'bg-success/12 text-success';
      case 'UPDATE':
        return 'bg-warning/12 text-warning';
      case 'DELETE':
        return 'bg-error/12 text-error';
      case 'LOGIN':
        return 'bg-info/12 text-info';
      case 'LOGOUT':
        return 'bg-muted-deep/12 text-muted-deep';
      default:
        return 'bg-surface-2 text-content-muted';
    }
  };

  const getRoleLabel = (role) => {
    switch ((role || '').toLowerCase()) {
      case 'admin':
        return 'Administrador';
      case 'operator':
        return 'Operador';
      case 'technician':
      case 'tecnico':
      case 'técnico':
        return 'Técnico';
      default:
        return role || '-';
    }
  };

  const truncateText = (value, maxLength = 20) => {
    if (value === null || value === undefined) return '-';
    const text = String(value);
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}...`;
  };



  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportLogs = async () => {
    try {
      const params = new URLSearchParams({
        ...filters,
        limit: 100000 // Para pedir todos los logs o dejar que el backend use su limite
      });

      const response = await fetch(`/api/audit/activity-logs/export?${params}`);
      
      if (!response.ok) {
        throw new Error('Error al descargar actividad');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity_logs_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exportando logs:', error);
    }
  };

  const safeTotal = Number.isFinite(total) ? total : 0;
  const totalPages = Math.max(1, Math.ceil(safeTotal / pageSize));

  const filterColumns = [
    { type: 'text', key: 'action', label: 'Acción' },
    { 
      type: 'select',
      key: 'userRole', 
      label: 'Rol de Usuario', 
      options: [
        { value: '', name: 'Todos los roles' },
        { value: 'admin', name: 'Administrador' },
        { value: 'operator', name: 'Operador' },
        { value: 'technician', name: 'Técnico' }
      ],
      optionValue: 'value',
      optionLabel: 'name'
    },
    { key: 'startDate', label: 'Fecha Inicio', type: 'date' },
    { key: 'endDate', label: 'Fecha Fin', type: 'date' }
  ];

  const tableColumns = [
    { key: 'createdAt', label: 'Fecha', render: (log) => formatDate(log.createdAt) },
    { key: 'userName', label: 'Usuario' },
    {
      key: 'action',
      label: 'Acción',
      render: (log) => {
        const actionText = log.action || '-';

        return renderResponsiveText({
          log,
          value: actionText,
          maxLength: 20,
          prefix: 'action',
          className: `inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getActionBadgeClass(actionText)}`
        });
      }
    },
    {
      key: 'description',
      label: 'Descripción',
      render: (log) => {
        const fullText = log.description || '-';
        const canCopyDescription = log.action === 'SUBIR_IMAGENES_GOOGLE_DRIVE' && fullText !== '-';
        const copyKey = getLogCopyKey(log);
        const isCopied = copiedDescriptionKey === copyKey;
        const descriptionCellKey = createTextCellKey('description', log);
        const shouldTruncateDescription = fullText !== '-' && fullText.length > 30;
        const isExpandedDescription = expandedTextKey === descriptionCellKey;

        if (!canCopyDescription) {
          return renderResponsiveText({
            log,
            value: fullText,
            maxLength: 30,
            prefix: 'description',
            alwaysTruncate: true
          });
        }

        const descriptionText = shouldTruncateDescription && !isExpandedDescription
          ? truncateText(fullText, 30)
          : fullText;

        return (
          <div
            className="w-full flex items-center justify-center gap-2 cursor-pointer hover:text-content-muted transition-colors rounded-none border-none bg-transparent p-0 shadow-none outline-none text-center"
            title="Click para copiar descripción"
          >
            <span
              title={fullText}
              role={shouldTruncateDescription && isMobileOnly ? 'button' : undefined}
              tabIndex={shouldTruncateDescription && isMobileOnly ? 0 : undefined}
              onClick={(event) => {
                event.stopPropagation();

                if (shouldTruncateDescription && isMobileOnly) {
                  handleTextCellToggle(descriptionCellKey);
                  return;
                }

                handleCopyDescription(log);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();

                  if (shouldTruncateDescription && isMobileOnly) {
                    handleTextCellToggle(descriptionCellKey);
                    return;
                  }

                  handleCopyDescription(log);
                }
              }}
              className={`${shouldTruncateDescription && isMobileOnly ? 'cursor-pointer' : 'cursor-help'} text-center`}
            >
              {descriptionText}
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                handleCopyDescription(log);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  handleCopyDescription(log);
                }
              }}
              className="flex-shrink-0"
              title="Copiar descripción"
            >
              {isCopied ? (
                <Check size={16} className="text-success" />
              ) : (
                <Copy size={16} className="text-content-muted" />
              )}
            </span>
          </div>
        );
      }
    },
    {
      key: 'ipAddress',
      label: 'IP',
      render: (log) => {
        const ipText = log.ipAddress || '-';

        return renderResponsiveText({
          log,
          value: ipText,
          maxLength: 12,
          prefix: 'ip',
          className: 'cursor-help'
        });
      }
    }
  ];

  return (
    <div className="space-y-6">
			<PageHeader
        title="Logs de Actividad"
				icon={<GearSix size={24} weight="duotone" />}
        subtitle="Consulta acciones, usuarios y trazabilidad del sistema"
        actions={(
          <button
            onClick={exportLogs}
            className="inline-flex items-center gap-2 rounded-lg bg-amber px-3 py-2 text-sm font-medium text-on-amber transition-colors hover:bg-amber-hover"
          >
            <Download size={16} />
            Exportar CSV
          </button>
        )}
			/>

			<GenericFilters
				columns={filterColumns}
				filters={filters}
        onFilterChange={(nextFilters) => {
          setFilters(nextFilters);
          setCurrentPage(1);
        }}
				onClearFilters={() => {
          setFilters({
            action: '',
            userRole: '',
            startDate: getTodayLocalDate(),
            endDate: ''
          });
          setPageSize(50);
          setCurrentPage(1);
        }}
				loading={loading}
			/>

			<div className="relative">
				<GenericTables
					columns={tableColumns}
					data={logs}
					emptyMessage="No hay logs de actividad disponibles"
          showFooter
          pagination={{
            currentPage,
            totalPages,
            pageSize,
            totalItems: total,
            pageSizeOptions: [25, 50, 100],
            onPageChange: setCurrentPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setCurrentPage(1);
            }
          }}
				/>

				{loading && (
					<div className="absolute inset-0 flex items-center justify-center bg-bg/60 rounded-xl">
						<div className="flex items-center gap-3 rounded-lg border border-line bg-surface/90 px-4 py-3 text-sm text-content shadow-lg">
							<span className="h-5 w-5 animate-spin rounded-full border-2 border-amber border-t-transparent" />
							Cargando logs de actividad...
						</div>
					</div>
				)}
			</div>
    </div>
  );
};

export default ActivityLogs;
